/**
 * KAN-862 — the device-local checkout marker `{ nonce, wasSubscribedBefore, startedAt }`, written the moment we leave
 * for Stripe. Kept apart from the receipt store so `useBillingActions` can write it without pulling the Supabase
 * client in. NOT a receipt (never synced); per user and tenant; holds no PII.
 */
export const SUBSCRIBE_CELEBRATED_PREFIX = 'cvx-subscribe-celebrated-v1:';
export const SUBSCRIBE_PENDING_PREFIX = 'cvx-subscribe-celebration-pending-v1:';
/** A started checkout can celebrate late for this long; after that it is forgotten. */
export const SUBSCRIBE_PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const subscribePendingKey = (userId, tenantId) => `${SUBSCRIBE_PENDING_PREFIX}${tenantId || 'tenant'}:${userId || 'anon'}`;

const listeners = new Set();
export function subscribeMarkerChanges(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
function notify() {
  listeners.forEach((fn) => { try { fn(); } catch (_e) { /* never break the others */ } });
}

const newNonce = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch { /* fall through */ }
  return `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
};

/** Written when checkout STARTS (we are about to leave for Stripe). Always a fresh nonce: a second checkout replaces the first. */
export function markSubscribeCheckoutStarted(userId, tenantId, { wasSubscribedBefore = false } = {}, storage, nowMs = Date.now()) {
  if (!userId || !tenantId) return null;
  const marker = { nonce: newNonce(), wasSubscribedBefore: wasSubscribedBefore === true, startedAt: nowMs };
  try {
    (storage || window.localStorage).setItem(subscribePendingKey(userId, tenantId), JSON.stringify(marker));
  } catch { return null; }
  notify();
  return marker;
}

/** The marker, or null (none / expired / unreadable / malformed). */
export function readSubscribeMarker(userId, tenantId, storage, nowMs = Date.now()) {
  if (!userId) return null;
  try {
    const m = JSON.parse((storage || window.localStorage).getItem(subscribePendingKey(userId, tenantId)) || 'null');
    if (!m || typeof m.nonce !== 'string' || !m.nonce || !Number.isFinite(m.startedAt)) return null;
    if (nowMs - m.startedAt >= SUBSCRIBE_PENDING_TTL_MS) return null;
    return { nonce: m.nonce, wasSubscribedBefore: m.wasSubscribedBefore === true, startedAt: m.startedAt };
  } catch {
    return null;
  }
}

export function clearSubscribePending(userId, tenantId, storage) {
  try { (storage || window.localStorage).removeItem(subscribePendingKey(userId, tenantId)); } catch { /* best-effort */ }
}
